import { Translation } from '@ps/web-shared-components';
import React, { useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';

import withWidgetTestId from '@swf/core/hoc/withWidgetTestId';
import { select } from '@swf/core/store';
import type { WidgetFC } from '@swf/core/types/Widget';
import { CardTitle, Message, TryAgainMessage } from '@swf/sports/components';
import GroupedTemplatePanels from '@swf/sports/components/GroupedTemplatePanels/GroupedTemplatePanels';
import LiveDataWarning from '@swf/sports/components/LiveDataWarning/LiveDataWarning';
import useMarketUpdates from '@swf/sports/hooks/useMarketUpdates';
import useScoreboardPolling from '@swf/sports/hooks/useScoreboardPolling/useScoreboardPolling';
import { getEventTypesWithInPlayEventIds } from '@swf/sports/utils/getEventTypesWithInPlayEventIds';
import getEventsFromMarketTemplateData from '@swf/sports/utils/getEventsFromMarketTemplateData';

import {
  TXTWCL_In_Play,
  TXTWCL_There_are_no_active_in_play_ev_ELL
} from './SportsInPlayEventsWidget.messages.json';
import styles from './SportsInPlayEventsWidget.scss';
import {
  GroupedTemplatePanelsLoadingState,
  TabsLoadingState
} from './components/LoadingState/LoadingState';
import Tabs from './components/Tabs/Tabs';
import { sportsInPlayEventsWidgetModel, useSportsInPlayEventsWidget } from './model';

const SportsInPlayEventsWidget: WidgetFC = () => {
  const { widgetDispatch, widgetSelect } = useSportsInPlayEventsWidget();
  const firstRender = useRef(true);

  const events = useSelector(widgetSelect.events);
  const markets = useSelector(widgetSelect.markets);
  const groups = useSelector(widgetSelect.groups);
  const competitions = useSelector(widgetSelect.competitions);
  const eventTypes = useSelector(widgetSelect.eventTypes);
  const marketIds = useSelector(widgetSelect.marketIds);
  const tabs = useSelector(widgetSelect.tabs);
  const error = useSelector(widgetSelect.error);
  const isLoading = useSelector(widgetSelect.isLoading);
  const { eventTypeId } = useSelector(select.sports.getUrlParams);

  const eventTypesWithEventIds = useMemo(
    () =>
      getEventTypesWithInPlayEventIds(
        getEventsFromMarketTemplateData(events, markets, competitions, eventTypes)
      ),
    [competitions, eventTypes, events, markets]
  );

  useScoreboardPolling(eventTypesWithEventIds);

  useEffect(() => {
    if (firstRender.current) {
      return;
    }

    widgetDispatch.inPlayTabChange();
  }, [widgetDispatch, eventTypeId]);

  // Must come after `useEffects` that rely on `firstRender.current === true`
  useEffect(() => {
    firstRender.current = false;
  }, []);

  useMarketUpdates(marketIds, widgetDispatch.marketsUpdated);

  let content: React.ReactNode;

  if (isLoading && !groups.length) {
    content = <GroupedTemplatePanelsLoadingState />;
  } else if (error === 404) {
    content = <Message message={<Translation {...TXTWCL_There_are_no_active_in_play_ev_ELL} />} />;
  } else if (typeof error !== 'undefined') {
    content = <TryAgainMessage />;
  } else if (!groups.length) {
    content = <Message message={<Translation {...TXTWCL_There_are_no_active_in_play_ev_ELL} />} />;
  } else {
    content = (
      <>
        <GroupedTemplatePanels groupedOn="COMPETITION" groups={groups} />
        <LiveDataWarning />
      </>
    );
  }

  const shouldShowLoadingState = isLoading && firstRender.current;

  return (
    <>
      <div className={styles.titleWrapper}>
        <CardTitle title={<Translation {...TXTWCL_In_Play} />} />
      </div>
      {shouldShowLoadingState ? <TabsLoadingState /> : null}
      {tabs.length && !shouldShowLoadingState ? (
        <Tabs tabs={tabs} exact={Boolean(eventTypeId)} />
      ) : null}
      {content}
    </>
  );
};

SportsInPlayEventsWidget.model = sportsInPlayEventsWidgetModel;

export default withWidgetTestId(SportsInPlayEventsWidget, 'sports-in-play-events');
